import { useCallback, useEffect, useMemo, useState } from 'react';
import lscache from 'lscache';
import { Content } from '../../apiService';
import { __t } from '../../i18n';
import UsePlayerProps from '../../Store/usePlayerProps';
import { Sources, VideoFileData } from '../Player/PlayerModels';
import { getVideoData, handleSources, techSupport } from '../Player/PlayerServices';
import { flattenEpisodes, getEpisodeUrl } from './shortEpisodes';

type ShortSourceParams = {
  content?: Content;
  fileId?: string;
  /** Warmed one ahead, so a swipe doesn't wait on the file lookup before it can buffer. */
  nextFileId?: string;
  /** A short the viewer can't watch: nothing is requested, the notice stands in for the player. */
  skip?: boolean;
};

/** The urls the API hands back are time-limited, so a warmed one is only worth a short reuse. */
const PREFETCH_TTL = 60_000;

// A single entry, consumed on use: it can't grow, and an episode revisited later always gets a
// fresh url rather than a replayed one.
let warmed: { key: string; at: number; data: VideoFileData } | null = null;

const keyFor = (contentId: string, fileId: string) => `${contentId}:${fileId}`;

const loadVideoData = (tech: string, contentId: string, fileId: string) => {
  const key = keyFor(contentId, fileId);

  if (warmed?.key === key && Date.now() - warmed.at < PREFETCH_TTL) {
    const { data } = warmed;
    warmed = null;
    return Promise.resolve(data);
  }

  // The refusals this can come back with — a paywalled episode, an age-restricted short — are
  // answered by a notice over the player, so the global snackbar would only repeat it. The
  // rest are reported by the stage's own retry screen.
  return getVideoData({ tech, contentId, videoFileId: fileId, handledByCaller: true });
};

/**
 * Loads one short episode into the shared player store.
 *
 * Both players read that store once, in a mount effect, so switching episodes has to unmount
 * and remount them. `ready` dropping while the next source is in flight gives exactly that,
 * and the players' own unmount clears the store before we refill it.
 */
export default function useShortSource({ content, fileId, nextFileId, skip }: ShortSourceParams) {
  const setPlayerState = UsePlayerProps((s) => s.setPlayerState);
  // The store still holds the previous episode for a render after the URL changes, so
  // comparing ids — rather than tracking a loading flag — is what says the data is this
  // episode's. It flips synchronously, so the player never renders against stale sources.
  const loadedFileId = UsePlayerProps((s) => s.playerState.contentData?.videoFileId);
  const hasSources = UsePlayerProps((s) => !!s.playerState.sources?.videos?.length);

  const [tech, setTech] = useState<string>();
  const [error, setError] = useState<any>(null);
  const [attempt, setAttempt] = useState(0);

  const ready = !skip && !!tech && hasSources && loadedFileId === fileId;

  const retry = useCallback(() => {
    setError(null);
    setAttempt((n) => n + 1);
  }, []);

  const episode = useMemo(
    () => flattenEpisodes(content).find((item) => item.file_id === fileId),
    [content, fileId],
  );

  useEffect(() => {
    if (!content || !fileId || skip) return;

    // techSupport() reads the persisted player_config, so it only resolves on the client.
    const resolvedTech = techSupport();
    let cancelled = false;
    setError(null);

    loadVideoData(resolvedTech, content.id, fileId)
      .then((configs) => {
        if (cancelled) return;
        const sources = handleSources(resolvedTech, content.id, configs) as Sources;
        // A file the API resolved but that carries no playable url would otherwise leave the
        // cover spinning with nothing to mount a player against.
        if (!sources.videos.length) throw new Error('short episode has no playable source');

        setTech(resolvedTech);
        setPlayerState({
          contentData: {
            id: content.id,
            titles: {
              primary: content.title,
              secondary: episode?.episode_title || content.original_name || '',
            },
            cover: episode?.image?.path || content.cover_image?.path,
            videoFileId: fileId,
            videoFileType: 'primary',
            contentUrl: getEpisodeUrl(content, fileId),
            content,
            // Empty on purpose: it drives the player's season/episode panel, which the shorts
            // episode grid replaces.
            seasons: [],
          },
          contentConfigs: configs,
          sources,
          captionState: {
            prevCaptionIndex: configs?.default_subtitle_index,
            selectedCaptionLang: { lang: 'off', label: __t('player.off') },
          },
          showingNextEpisode: lscache.get('player_config')?.autoNextEpisode ?? true,
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      });

    return () => {
      cancelled = true;
    };
  }, [content, episode, fileId, attempt, skip, setPlayerState]);

  // Warmed only once the current episode is playing, so it never competes for bandwidth with
  // the video the viewer is actually watching.
  useEffect(() => {
    if (!content || !nextFileId || !tech || !ready) return;

    const key = keyFor(content.id, nextFileId);
    if (warmed?.key === key) return;

    let cancelled = false;
    // Nothing is shown for a warm-up either way, so its failures stay silent entirely.
    getVideoData({ tech, contentId: content.id, videoFileId: nextFileId, handledByCaller: true })
      .then((data) => {
        if (!cancelled) warmed = { key, at: Date.now(), data };
      })
      // A warm-up that fails is not worth surfacing; the real load will report it.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [content, nextFileId, tech, ready]);

  return { tech, error, ready, retry };
}
